import React from "react";
import { cn } from "@/lib/utils";
import { Request } from "@/types/research.types"; // Assuming Request type location
import { Command, CommandGroup, CommandItem, CommandList } from "./ui/command";

interface RequestCommandMenuProps {
  heading: string;
  requests: Request[];
  onSelect: (content: string) => void;
  selectedId: string;
}

const RequestCommandMenu: React.FC<RequestCommandMenuProps> = ({
  heading,
  requests,
  onSelect,
  selectedId,
}) => {
  const listRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (listRef.current) {
      const selectedElement = listRef.current.querySelector('[data-selected="true"]');

      if (selectedElement) {
        selectedElement.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    }
  }, [selectedId]);

  return (
    <Command shouldFilter={false} value={selectedId} className="w-full overflow-hidden rounded-md">
      <CommandList
        ref={listRef}
        className="flex flex-col h-full w-full overflow-hidden bg-popover text-popover-foreground max-h-[300px] overflow-y-auto overflow-x-hidden p-1 scroll-py-1"
      >
        <CommandGroup
          heading={heading}
          className="overflow-visible p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:sticky [&_[cmdk-group-heading]]:top-0 [&_[cmdk-group-heading]]:z-10 [&_[cmdk-group-heading]]:bg-neutral-700 [&_[cmdk-group-heading]]:text-sm [&_[cmdk-group-heading]]:font-semibold"
        >
          {requests.length === 0 ? (
            <div className="py-6 text-center text-sm">No requests found.</div>
          ) : (
            requests.map((req) => (
              <CommandItem
                key={req.id}
                value={req.id}
                className={cn(
                  "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none",
                  "data-[selected=true]:bg-primary-300 data-[selected=true]:text-neutral-white",
                  "text-neutral-900" // Default text color
                )}
                onSelect={() => onSelect(req.content)}
              >
                {req.name}
              </CommandItem>
            ))
          )}
        </CommandGroup>
      </CommandList>
      <div className="h-px bg-border -mx-1 my-1" />
      <div className="border-t bg-accent/50 p-2 text-xs text-muted-foreground flex items-center justify-between px-3">
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1">
            <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
              ↑
            </kbd>{" "}
            <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
              ↓
            </kbd>{" "}
            to navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
              ↵
            </kbd>{" "}
            to select
          </span>
        </div>
        <span className="flex items-center gap-1">
          <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
            esc
          </kbd>{" "}
          to close
        </span>
      </div>
    </Command>
  );
};

export default RequestCommandMenu;
